import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, startWith, switchMap, take, tap } from "rxjs/operators";
import { ContactInformation, CustomerOverviewModel, PaginationPage } from "src/app/classes";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { Address } from "src/app/lib/location/classes";
import { LocationService } from "src/app/lib/location/services/location.service";
import { IConcurrencyProviderService } from "src/app/lib/shared/interfaces";
import { Contact, Customer } from "../../../classes";
import { ICustomersProviderService, IDepartmentsProviderService } from "../../../interfaces";
import { ContactService } from "../../../services/contact.service";
import { CustomerService } from "../../../services/customer.service";

@Injectable()
export class ContactDetailViewComponentService implements IConcurrencyProviderService, ICustomersProviderService, IDepartmentsProviderService {

    private _checkingVersion = new BehaviorSubject(false);
    checkingVersion$: Observable<boolean> = this._checkingVersion.asObservable();

    private _contactId = new ReplaySubject(1);
    contactId$ = this._contactId.asObservable();
    entityIdentifier$ = this.contactId$;

    private _contact = new ReplaySubject(1);
    contact$ = this._contact.asObservable();
    concurrencyStamp$ = this._contact.pipe(map((contact: Contact) => contact.concurrencystamp));

    private _departments = new BehaviorSubject([]);
    departments$ = this._departments.asObservable();

    private _departmentPaginationPage = new BehaviorSubject<PaginationPage>({ 'pageNumber': 1, 'pageSize': 20 });
    private _departmentTotalItems = new BehaviorSubject(0);
    private _hasMoreDepartments = new BehaviorSubject(false);

    departmentPaginationPage$ = this._departmentPaginationPage.asObservable();
    departmentTotalItems$ = this._departmentTotalItems.asObservable();
    hasMoreDepartments$ = this._hasMoreDepartments.asObservable();

    private _loading = new Subject<boolean>();
    loading$ = this._loading.asObservable();

    private _customers = new BehaviorSubject<Customer[]>([]);
    customers$ = this._customers.asObservable();

    validStamp$: Observable<boolean> = this._contact.pipe(switchMap((x: Contact) => {
        this._checkingVersion.next(true);
        return this._contactService.checkContactVersion(x).pipe(finalize(() => this._checkingVersion.next(false)));
    }));

    private _currentContactSub: Subscription = null;
    private _subscriptions: Subscription[] = [];

    constructor(
        private _contactService: ContactService,
        private _customerService: CustomerService,
        private _locationService: LocationService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.setUp();
    }

    deleteCurrentContact(): Observable<Object> {
        let subject = new Subject();
        this.contactId$
            .pipe(take(1), switchMap((contactId: number) => this._contactService.deleteContact(contactId)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    dispose(): void {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentContact();
    }

    linkToCustomer(customer: Customer): Observable<Object> {
        return this.contactId$.pipe(take(1))
            .pipe(
                switchMap((contactId: number) => this._customerService.linkContact(customer.customerid, contactId)),
                switchMap(() => this.refreshCurrentContact())
            );
    }

    loadMoreDepartments(): void {
        this.departmentPaginationPage$.pipe(take(1)).subscribe((page: PaginationPage) => {
            page.pageNumber++;
            this._departmentPaginationPage.next(page);
        });
    }

    refreshCurrentContact(): Observable<Contact> {
        let subject = new Subject<Contact>();
        this._contactId.pipe(
            take(1),
            switchMap((x: number) => this._contactService.getContactById(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentCustomers(): Observable<CustomerOverviewModel[]> {
        let subject = new Subject<CustomerOverviewModel[]>();
        this._contactId.pipe(
            take(1),
            switchMap((x: number) => this._contactService.getContactCustomers(x)),
            tap((customers: CustomerOverviewModel[]) => this._customers.next(customers as any))
        ).subscribe(subject);
        return subject.asObservable();
    }

    saveChanges(contact: Contact = null, note: string = null, address: Address = null, contactInformation: ContactInformation[], deletedContactInformation: number[]): Observable<Contact> {

        return (address ? this._locationService[(typeof address.addressid === 'number' && address.addressid !== 0) ? 'updateAddress' : 'createAddress'](address) : of(address))
            .pipe(
                switchMap((address: Address) => {
                    contact.contactpersaddress = address.addressid;
                    return this._contactService.updateContact(contact, note).pipe(
                        switchMap((result: Contact) => {
                            if (contactInformation.length > 0) {
                                contactInformation.forEach(x => x.infocpers = result.contactpersid);
                                return combineLatest(contactInformation.map(x => this._contactService[x.infoid ? 'updateContactInformation' : 'createContactInformation'](x)))
                                    .pipe(map((args) => result));
                            }
                            else return of(result);
                        }),
                        switchMap((result: Contact) => {
                            if (deletedContactInformation.length > 0) {
                                return combineLatest(deletedContactInformation.map(x => this._contactService.deleteContactInformation(x))).pipe(map((args) => result));
                            }
                            else return of(result);
                        })
                    )
                })
            );
    }

    setContactId(contactId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentContact();
        this._currentContactSub = combineLatest([this._contactService.getContactById(contactId, false), this._contactService.getContactCustomers(contactId)]).pipe(take(1)).subscribe({
            next: ([contact, customers]) => {
                this._contact.next(contact);
                this._customers.next(customers as any);
                this._loading.next(false);
            },
            error: (error: HttpErrorResponse) => {
                switch(error.status){
                    case 404:
                        this._dialog.open(ErrorDialogComponent, {
                            panelClass: 'no-padding-dialog-container',
                            disableClose: true,
                            data: {
                                'description': `Es konnte kein Ansprechpartner mit der ID ${contactId} gefunden werden. Ist der Link eventuell veraltet oder wurde der Ansprechpartner in der Zwischenzeit gelöscht?`,
                                'error': error,
                                'showNavigateToMainPage': true
                            } as ErrorWrapper
                        });
                        break;
                }
                this._loading.next(false);
            }
        });
    }

    setUp(): void {
        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((contactId: number) => {
                this._contactId.next(contactId);
            }),
            this.contactId$.pipe(distinctUntilChanged()).subscribe((identifier: number) => this.setContactId(identifier)),
            this.departmentPaginationPage$.pipe(switchMap((page: PaginationPage) => this._contactService.getDepartmentSuggestions(page)))
                .pipe(switchMap((result) => combineLatest([of(result), this.departments$]).pipe(take(1))))
                .subscribe(([result, departments]) => {
                    if (result.page.pageNumber === 1) departments = [];
                    departments = [...departments, ...result.content];
                    this._departmentTotalItems.next(result.totalItems);
                    this._departments.next(departments);
                    this._hasMoreDepartments.next(departments.length < result.totalItems);
                })
        ]);
    }

    unlinkCustomer(customer: Customer): Observable<Object> {
        return this.contactId$.pipe(take(1))
            .pipe(
                switchMap((contactId: number) => this._customerService.unlinkContact(customer.customerid, contactId)),
                switchMap(() => this.refreshCurrentContact())
            );
    }

    private _unsubscribeCurrentContact() {
        if (this._currentContactSub) {
            this._currentContactSub.unsubscribe();
            this._currentContactSub = null;
        }
    }

}
